import { useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { useAuth } from '../hooks/useAuth';
import { PageContainer } from '../components/PageContainer';
import { Button } from '../components/ui/Button';
import { TastingExperienceForm } from '../components/TastingExperienceForm';
import { UserRole, type TastingExperienceStatus } from '@wineryxplorer/shared';
import type { TastingExperienceFormData } from '../types/tastingExperience';

// Type for the tasting experience data from the API
interface TastingExperienceData {
  id: string;
  name: string;
  description: string;
  venueId: string | { _id?: string; id?: string };
  price: {
    amount: number;
    currency: string;
  };
  duration?: number;
  wines?: string[];
  requiresReservation: boolean;
  availability?: string;
  heroImageId?: string;
  imageIds: string[];
  videoIds: string[];
  tags: string[];
  wineClubOnly: boolean;
  status: TastingExperienceStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export const TastingExperienceEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const formSubmitRef = useRef<(() => Promise<void>) | null>(null);

  // Fetch tasting experience data
  const { data: experienceData, isLoading, error: fetchError } = trpc.tastingExperience.getById.useQuery(
    { id: id || '' },
    { enabled: !!id }
  ) as { data: TastingExperienceData | undefined; isLoading: boolean; error: Error | null };

  // Extract venueId early - handle both string and populated object
  let venueIdString: string | undefined;
  if (experienceData?.venueId) {
    if (typeof experienceData.venueId === 'string') {
      venueIdString = experienceData.venueId;
    } else if (typeof experienceData.venueId === 'object') {
      // Handle populated venue object
      const venueObj = experienceData.venueId as unknown as { _id?: string; id?: string };
      venueIdString = venueObj._id || venueObj.id;
    }
  }

  // The venue management hub links here with `from=manage`, which is the only
  // signal that this edit began there rather than on the experience's own page.
  const [searchParams] = useSearchParams();
  const cameFromManageHub = searchParams.get('from') === 'manage';

  // Back to wherever this edit started: the venue's management hub, or the
  // experience's own page.
  const exitPath = () =>
    cameFromManageHub && venueIdString
      ? `/wineries/${venueIdString}/manage`
      : `/tasting-experiences/${id}`;

  // Update mutation
  const updateExperience = trpc.tastingExperience.update.useMutation({
    onSuccess: () => {
      navigate(exitPath());
    },
    onError: (err) => {
      setError(err.message);
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  });

  // Check if user has permission to edit
  const canEdit =
    user &&
    experienceData &&
    (user.role === UserRole.SUPER_ADMIN ||
      user.role === UserRole.SITE_ADMIN ||
      user.id === experienceData.createdBy);

  const handleSubmit = async (data: TastingExperienceFormData) => {
    if (!id) return;
    
    setError(null);
    await updateExperience.mutateAsync({
      id,
      ...data,
    });
  };

  const handleSaveClick = async () => {
    if (formSubmitRef.current) {
      await formSubmitRef.current();
    }
  };

  const handleCancel = () => {
    navigate(exitPath());
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer className="mt-2">
          <div className="text-center py-8">
            <p className="text-gray-600">Loading tasting experience...</p>
          </div>
        </PageContainer>
      </div>
    );
  }

  // Error state
  if (fetchError || !experienceData) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer className="mt-2">
          <div className="text-center py-8">
            <p className="text-red-600">
              {fetchError?.message || 'Tasting experience not found'}
            </p>
          </div>
        </PageContainer>
      </div>
    );
  }

  // Permission check
  if (!canEdit) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer className="mt-2">
          <div className="text-center py-8">
            <p className="text-red-600">
              You do not have permission to edit this tasting experience
            </p>
          </div>
        </PageContainer>
      </div>
    );
  }

  // Prepare initial data for form
  const initialData: Partial<TastingExperienceFormData> = {
    name: experienceData.name,
    description: experienceData.description,
    venueId: venueIdString,
    price: experienceData.price,
    duration: experienceData.duration,
    wines: experienceData.wines,
    requiresReservation: experienceData.requiresReservation,
    availability: experienceData.availability,
    heroImageId: experienceData.heroImageId,
    imageIds: experienceData.imageIds,
    videoIds: experienceData.videoIds,
    tags: experienceData.tags,
    wineClubOnly: experienceData.wineClubOnly,
    status: experienceData.status,
  };

  return (
    <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
      <PageContainer className="mt-2">
        <div className="border-b border-gray-200 pb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Edit Tasting Experience</h1>
          
          {/* Action Buttons - Top of Page */}
          <div className="flex gap-4 mb-6 justify-end">
            <Button
              variant="primary"
              onClick={handleSaveClick}
              disabled={updateExperience.isPending}
              loading={updateExperience.isPending}
            >
              {updateExperience.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              variant="outline"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
            <div className="flex">
              <svg
                className="w-5 h-5 text-red-400 mr-3 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800">
                  Error updating tasting experience
                </h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tasting Experience Form */}
        <div className="mt-6">
          <TastingExperienceForm
            initialData={initialData}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={updateExperience.isPending}
            hideButtons
            submitRef={formSubmitRef}
          />
        </div>
      </PageContainer>
    </div>
  );
};
